/**
 * Genehmigungen — Sammelmappe (Joerg 2026-07-07).
 *
 * Portal-weite Freigabe-Inbox fuer die Geschaeftsfuehrung: aggregiert
 * offene Freigaben aus mehreren Modulen. MVP: Zeiterfassung (Abwesenheits-
 * Antraege + Zeitkorrekturen) + Baustelle (Aufgaben ohne Owner). Rechnungen/
 * Angebote + Fuhrpark-Schaeden sind Platzhalter fuer Ausbaustufe 2.
 *
 * Berichts-Verifizierung gehoert bewusst NICHT rein — das ist Selbst-Aufgabe
 * des Erstellers, kein GF-Vorgang.
 *
 * Folgeaufgaben-Vorschläge (Jörg 2026-10-28: „neue Aufgaben sollen direkt auf der
 * Genehmigungsseite für Büro und Geschäftsführung auftauchen"): Sektion Baustelle,
 * „Anlegen" öffnet die Aufgabenmaske aus bauberichteV2/public vorbefüllt (Mitarbeiter =
 * Berichtsersteller), gespeichert verknüpft das Backend den Vorschlag; „Verwerfen" mit Rückfrage.
 *
 * Datenquelle: GET /api/portal/genehmigungen/inbox
 * Aktions-Endpoints: PUT /api/modules/zeiterfassung/admin/{absences,corrections}/:id
 * Sichtbarkeit: Adressbuch-Gruppe „Geschaeftsfuehrung" oder Admin
 * (Backend-403 → Redirect auf /).
 *
 * Dokument-Navigation (Abhakliste 2.92, 2026-10-01): Zeitraum- und Modul-Filter
 * ersetzend in der Adresse, Scroll-Wiederherstellung, Seitentitel. Genehmigen und
 * Ablehnen bleiben Sofort-Aktionen (Ausnahme 1) — mit Rückfrage, sichtbarer Rückmeldung
 * und Fehler als Meldung; der Ablehn-Dialog hat Dialog-Schutz.
 */
import { useCallback, useEffect, useMemo, useState, type ComponentType } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  CheckCircle2, XCircle, ExternalLink, Loader2, RefreshCw, Clock, Hammer,
  Receipt, Truck, AlertCircle, ClipboardList, Plus, Trash2,
} from 'lucide-react';
import { api } from '@onvice/portal-shell-api';
import { GuardedModal, param, useDialogs, useListState, usePageLabel, useScrollRestore } from '@onvice/portal-ui';
import { useFederatedModule } from '../lib/useFederatedModule';

// TaskEditModal aus bauberichteV2 nur dynamisch (Regel feedback_federation_no_static_imports).
type TaskEditModalProps = {
  task: null;
  open: boolean;
  initialTitle: string | null;
  initialDescription: string | null;
  initialDate: string | null;
  initialCustomerId: string | null;
  initialUserId: string | null;
  onClose: () => void;
  onSaved: (createdTaskId?: string) => void;
};
type BauberichteV2Public = { TaskEditModal: ComponentType<TaskEditModalProps> };

type UrgencyLevel = 'ok' | 'warn' | 'urgent';
type ChipKind = 'info' | 'warn' | 'urgent';

interface Chip { kind: ChipKind; text: string }

interface ActionSpec {
  path: string;
  method: 'PUT' | 'POST';
  body: Record<string, unknown>;
  needsComment?: boolean;
}

interface Item {
  id: string;
  module: string;
  kind: string;
  primary: string;
  chips: Chip[];
  meta: string;
  context?: string;
  ageLabel: string;
  ageHours: number;
  urgencyLevel: UrgencyLevel;
  openHref: string;
  approve?: ActionSpec;
  reject?: ActionSpec;
  /** Folgeaufgaben-Vorschlag (kind 'task-suggestion'). */
  suggestion?: {
    title: string;
    description: string | null;
    startDate: string | null;
    contactId: string | null;
    userId: string;
    acceptPath: string;
    dismissPath: string;
  };
}

interface SubCategory { key: string; label: string; count: number; items: Item[] }

interface Section {
  key: string;
  label: string;
  icon: string;
  moduleHref?: string;
  counts: { total: number; urgent: number };
  subs?: SubCategory[];
  placeholder?: boolean;
  placeholderText?: string;
}

interface InboxResponse {
  success: boolean;
  data: {
    counts: { total: number; urgent: number; today: number };
    sections: Section[];
  };
  error?: string;
}

const ICON_MAP: Record<string, React.ComponentType<{ size?: number; className?: string }>> = {
  Clock, Hammer, Receipt, Truck,
};

const CHIP_STYLE: Record<ChipKind, string> = {
  info: 'bg-sky-500/15 text-sky-300 border-sky-500/30',
  warn: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  urgent: 'bg-red-500/15 text-red-300 border-red-500/30',
};

const URGENCY_LABEL_STYLE: Record<UrgencyLevel, string> = {
  ok: 'text-slate-400',
  warn: 'text-amber-300',
  urgent: 'text-red-300',
};

type FilterModule = 'alle' | 'zeiterfassung' | 'bauberichte' | 'rechnungen' | 'fuhrpark';
type FilterTime = 'offen' | 'ueberfaellig' | 'heute' | 'woche';

/** Listenzustand in der Adresse (R2): Zeitraum `zeit`, Modul `modul`. */
const GENEHMIGUNGEN_LIST = {
  zeit: param.oneOf(['offen', 'ueberfaellig', 'heute', 'woche'] as const, 'offen'),
  modul: param.oneOf(['alle', 'zeiterfassung', 'bauberichte', 'rechnungen', 'fuhrpark'] as const, 'alle'),
};

export default function GenehmigungenPage() {
  const navigate = useNavigate();
  const dlg = useDialogs();
  usePageLabel('Genehmigungen', 'list');
  const [data, setData] = useState<InboxResponse['data'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);
  const [list, setList] = useListState(GENEHMIGUNGEN_LIST);
  const filterModule: FilterModule = list.modul;
  const filterTime: FilterTime = list.zeit;
  const setFilterModule = (m: FilterModule): void => { setList({ modul: m }); };
  const setFilterTime = (t: FilterTime): void => { setList({ zeit: t }); };
  useScrollRestore(!loading && data != null);
  // Rückmeldung der letzten Sofort-Aktion (Mindeststandard E4).
  const [notice, setNotice] = useState<string | null>(null);

  const [rejectTarget, setRejectTarget] = useState<{ item: Item } | null>(null);
  const [rejectComment, setRejectComment] = useState('');
  const [rejectBusy, setRejectBusy] = useState(false);

  // Folgeaufgaben-Vorschlag: „Anlegen" öffnet die Aufgabenmaske vorbefüllt.
  const bv2 = useFederatedModule<BauberichteV2Public>('bauberichteV2/public');
  const TaskEditModal = bv2?.TaskEditModal ?? null;
  const [suggestionTarget, setSuggestionTarget] = useState<Item | null>(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      // apiFetch liefert die volle Response {success, data} — nicht nur data.
      const r = await api.get<InboxResponse>('/portal/genehmigungen/inbox');
      if (r.success) setData(r.data);
      else setError(r.error ?? 'Sammelmappe konnte nicht geladen werden.');
    } catch (err) {
      const status = (err as { status?: number }).status;
      if (status === 403) {
        navigate('/', { replace: true });
        return;
      }
      setError((err as Error).message ?? 'Fehler beim Laden');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    void load();
    const t = window.setInterval(() => void load(true), 30_000);
    return () => window.clearInterval(t);
  }, [load, reloadTick]);

  function showNotice(text: string): void {
    setNotice(text);
    window.setTimeout(() => setNotice((cur) => (cur === text ? null : cur)), 4000);
  }

  // Einzel-Aktion (Ausnahme 1): schreibt sofort — nach Rückfrage, mit Rückmeldung; bei einem
  // Fehler bleibt die Zeile stehen und die Meldung erscheint als Dialog.
  async function runAction(item: Item, spec: ActionSpec): Promise<void> {
    const ok = await dlg.confirm({
      title: 'Genehmigen?',
      message: `„${item.primary}“ wird genehmigt und verschwindet aus der Sammelmappe.`,
      confirmLabel: 'Genehmigen',
    });
    if (!ok) return;
    setBusy(`${item.id}:${spec.body['decision']}`);
    try {
      // spec.path beginnt mit /modules/… — api.put/post prefixt selbst /api,
      // deshalb hier KEIN manuelles /api davor (Doppel-Praefix /api/api/ → 404).
      // STAT-P7 (2026-08-07): Bericht-Approve nutzt POST, Absences/Corrections PUT.
      const call = spec.method === 'POST' ? api.post : api.put;
      await call(spec.path, spec.body);
      showNotice(`Genehmigt: ${item.primary}`);
      // Silent-Reload, damit die Zeile aus der Sammelmappe verschwindet.
      setReloadTick((t) => t + 1);
    } catch (err) {
      await dlg.error('Genehmigen fehlgeschlagen', err);
    } finally {
      setBusy(null);
    }
  }

  async function suggestionSaved(createdTaskId?: string): Promise<void> {
    const item = suggestionTarget;
    setSuggestionTarget(null);
    if (!item?.suggestion || !createdTaskId) return;
    try {
      // taskId-Modus: Backend verknüpft nur, legt nichts doppelt an.
      await api.post(item.suggestion.acceptPath, { taskId: createdTaskId });
      showNotice(`Aufgabe angelegt: ${item.primary}`);
    } catch (err) {
      await dlg.error('Aufgabe angelegt, Vorschlag aber nicht abgehakt', err);
    } finally {
      setReloadTick((t) => t + 1);
    }
  }

  async function dismissSuggestion(item: Item): Promise<void> {
    if (!item.suggestion) return;
    const ok = await dlg.confirm({
      title: 'Vorschlag verwerfen?',
      message: `„${item.primary}“ wird nicht als Aufgabe angelegt und verschwindet aus der Sammelmappe.`,
      confirmLabel: 'Verwerfen',
      danger: true,
    });
    if (!ok) return;
    setBusy(`${item.id}:verwerfen`);
    try {
      await api.post(item.suggestion.dismissPath, {});
      showNotice(`Verworfen: ${item.primary}`);
      setReloadTick((t) => t + 1);
    } catch (err) {
      await dlg.error('Verwerfen fehlgeschlagen', err);
    } finally {
      setBusy(null);
    }
  }

  function openReject(item: Item): void {
    setRejectTarget({ item });
    setRejectComment('');
  }

  async function confirmReject(): Promise<void> {
    if (!rejectTarget || rejectBusy) return;
    const comment = rejectComment.trim();
    if (!comment) return;
    const spec = rejectTarget.item.reject;
    if (!spec) return;
    setRejectBusy(true);
    try {
      // Kein manuelles /api davor (siehe runAction). Method aus spec respektieren.
      const call = spec.method === 'POST' ? api.post : api.put;
      await call(spec.path, { ...spec.body, comment });
      showNotice(`Abgelehnt: ${rejectTarget.item.primary}`);
      setRejectTarget(null);
      setReloadTick((t) => t + 1);
    } catch (err) {
      await dlg.error('Ablehnen fehlgeschlagen', err);
    } finally {
      setRejectBusy(false);
    }
  }

  const filteredSections = useMemo(() => {
    if (!data) return [];
    return data.sections
      .filter((s) => filterModule === 'alle' || s.key === filterModule)
      .map((s) => {
        if (!s.subs) return s;
        const subs = s.subs.map((sub) => ({
          ...sub,
          items: sub.items.filter((it) => {
            if (filterTime === 'offen') return true;
            if (filterTime === 'ueberfaellig') return it.urgencyLevel === 'urgent';
            if (filterTime === 'heute') return it.ageHours < 24 && it.ageHours > 0;
            if (filterTime === 'woche') return it.ageHours <= 24 * 7;
            return true;
          }),
        }));
        return { ...s, subs };
      });
  }, [data, filterModule, filterTime]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!data) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-300 flex flex-wrap items-center gap-3" data-test-anchor="load-error">
          <span>{error ?? 'Sammelmappe konnte nicht geladen werden.'}</span>
          <button type="button" onClick={() => void load()} className="underline">Erneut laden</button>
        </div>
      </div>
    );
  }

  const c = data.counts;

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-4" data-test-anchor="list" data-zeit={filterTime} data-modul={filterModule}>
      {/* Header + Stat-Chips */}
      <header className="space-y-3">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-slate-100 flex items-center gap-2">
              <ClipboardList size={22} className="text-brand-300" /> Genehmigungen
            </h1>
            <p className="text-sm text-slate-400 mt-1">
              Alle offenen Freigaben aus allen Modulen. Reihenfolge: ältester zuerst. Aktualisiert sich alle 30 s.
            </p>
          </div>
          <button
            type="button"
            onClick={() => void load()}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-800 text-slate-300 hover:bg-slate-800 text-sm disabled:opacity-50"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Aktualisieren
          </button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <StatCard label="Offen gesamt" value={c.total} />
          <StatCard label="Überfällig >3 Tage" value={c.urgent} tone={c.urgent > 0 ? 'red' : 'neutral'} />
          <StatCard label="Heute eingegangen" value={c.today} tone="sky" />
        </div>
      </header>

      {/* Filter */}
      <section className="flex items-center gap-2 flex-wrap py-1 text-xs" data-test-anchor="filter">
        <span className="text-[10px] uppercase tracking-wider text-slate-500 mr-1">Zeitraum</span>
        <FilterChip active={filterTime === 'offen'} onClick={() => setFilterTime('offen')}>Alles offen ({c.total})</FilterChip>
        <FilterChip active={filterTime === 'ueberfaellig'} onClick={() => setFilterTime('ueberfaellig')}>Überfällig ({c.urgent})</FilterChip>
        <FilterChip active={filterTime === 'heute'} onClick={() => setFilterTime('heute')}>Heute ({c.today})</FilterChip>
        <FilterChip active={filterTime === 'woche'} onClick={() => setFilterTime('woche')}>Diese Woche</FilterChip>

        <span className="text-[10px] uppercase tracking-wider text-slate-500 mx-2 ml-4">Modul</span>
        <FilterChip active={filterModule === 'alle'} onClick={() => setFilterModule('alle')}>Alle</FilterChip>
        {data.sections.map((s) => (
          <FilterChip
            key={s.key}
            active={filterModule === (s.key as FilterModule)}
            onClick={() => setFilterModule(s.key as FilterModule)}
          >
            {s.label} ({s.counts.total})
          </FilterChip>
        ))}
      </section>

      {error && (
        <div className="p-3 rounded-lg bg-red-950/40 border border-red-900/50 text-red-300 text-sm" data-test-anchor="load-error">{error}</div>
      )}
      {notice && (
        <div className="p-3 rounded-lg bg-emerald-950/40 border border-emerald-900/50 text-emerald-300 text-sm flex items-center gap-2" data-test-anchor="notice" data-state="saved">
          <CheckCircle2 size={14} /> {notice}
        </div>
      )}

      {/* Sektionen */}
      {filteredSections.map((s) => (
        <SectionCard
          key={s.key}
          section={s}
          busy={busy}
          onApprove={runAction}
          onReject={openReject}
          onCreateSuggestion={TaskEditModal ? setSuggestionTarget : null}
          onDismissSuggestion={(it) => void dismissSuggestion(it)}
        />
      ))}

      {TaskEditModal && (
        <TaskEditModal
          task={null}
          open={suggestionTarget !== null}
          initialTitle={suggestionTarget?.suggestion?.title ?? null}
          initialDescription={suggestionTarget?.suggestion?.description ?? null}
          initialDate={suggestionTarget?.suggestion?.startDate ?? null}
          initialCustomerId={suggestionTarget?.suggestion?.contactId ?? null}
          initialUserId={suggestionTarget?.suggestion?.userId ?? null}
          onClose={() => setSuggestionTarget(null)}
          onSaved={(id) => void suggestionSaved(id)}
        />
      )}

      {/* Ablehn-Modal — Dialog-Schutz: Klick daneben, X und Escape fragen nach, sobald ein Kommentar steht */}
      {rejectTarget && (
        <GuardedModal
          open
          title={`Ablehnen — ${rejectTarget.item.primary}`}
          label="Ablehnung"
          dirty={rejectComment.trim().length > 0}
          busy={rejectBusy}
          onClose={() => { setRejectTarget(null); setRejectComment(''); }}
          size="sm"
          testAnchor="reject-dialog"
          footer={(requestClose) => (
            <>
              <button
                type="button"
                onClick={requestClose}
                disabled={rejectBusy}
                className="px-3 py-2 rounded-lg text-sm text-slate-400 hover:bg-slate-800 min-h-[44px]"
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={() => void confirmReject()}
                disabled={rejectBusy || !rejectComment.trim()}
                data-action="reject"
                className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm bg-red-500/20 border border-red-500/40 text-red-200 hover:bg-red-500/40 disabled:opacity-60 min-h-[44px]"
              >
                {rejectBusy ? <Loader2 size={14} className="animate-spin" /> : <XCircle size={14} />} Ablehnen
              </button>
            </>
          )}
        >
          <p className="text-xs text-slate-400 mb-2">Bitte gib einen Kommentar mit — der Antragsteller sieht ihn als Begründung.</p>
          <textarea
            value={rejectComment}
            onChange={(e) => setRejectComment(e.target.value)}
            rows={4}
            placeholder="Begründung…"
            className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/40"
            autoFocus
          />
        </GuardedModal>
      )}
    </div>
  );
}

function StatCard({ label, value, tone = 'neutral' }: { label: string; value: number; tone?: 'neutral' | 'red' | 'sky' }) {
  const color = tone === 'red' ? 'text-red-300' : tone === 'sky' ? 'text-sky-300' : 'text-slate-100';
  return (
    <div className="p-3 rounded-lg bg-slate-900/50 border border-slate-800">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-2xl font-bold tabular-nums leading-tight ${color}`}>{value}</div>
    </div>
  );
}

function FilterChip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-2.5 py-1 rounded-full border transition-colors ${
        active
          ? 'bg-blue-500/20 text-blue-200 border-blue-500/50'
          : 'bg-slate-900/60 text-slate-400 border-slate-800 hover:border-slate-600'
      }`}
    >
      {children}
    </button>
  );
}

interface ItemHandlers {
  busy: string | null;
  onApprove: (item: Item, spec: ActionSpec) => void;
  onReject: (item: Item) => void;
  /** null = Aufgabenmaske (bauberichteV2) gerade nicht verfügbar. */
  onCreateSuggestion: ((item: Item) => void) | null;
  onDismissSuggestion: (item: Item) => void;
}

function SectionCard({ section, ...handlers }: { section: Section } & ItemHandlers) {
  const Icon = ICON_MAP[section.icon] ?? AlertCircle;
  const empty = !section.placeholder && (!section.subs || section.subs.every((sub) => sub.items.length === 0));
  return (
    <section className="rounded-xl border border-slate-800 bg-slate-900/40 overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-800 bg-slate-900/60 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon size={18} className="text-slate-300" />
          <h2 className="font-semibold text-slate-100">{section.label}</h2>
          <span className="pill-count">{section.counts.total} offen</span>
          {section.counts.urgent > 0 && (
            <span className="pill-count-urgent">{section.counts.urgent} überfällig</span>
          )}
        </div>
        {section.moduleHref && (
          <Link to={section.moduleHref} className="text-xs text-slate-400 hover:text-slate-200 inline-flex items-center gap-1">
            Modul öffnen <ExternalLink size={12} />
          </Link>
        )}
      </div>

      {section.placeholder ? (
        <div className="px-4 py-6 text-sm text-slate-500 italic">{section.placeholderText}</div>
      ) : empty ? (
        <div className="px-4 py-6 text-sm text-slate-500 italic">Nichts offen.</div>
      ) : (
        section.subs?.map((sub) => (
          sub.items.length === 0 ? null : (
            <div key={sub.key}>
              <div className="px-4 py-2 bg-slate-900/40 border-b border-slate-800/60 flex items-center gap-2">
                <span className="text-xs text-slate-500 uppercase tracking-wider">{sub.label}</span>
                <span className="pill-count">{sub.items.length}</span>
              </div>
              <ul className="divide-y divide-slate-800/40">
                {sub.items.map((it) => (
                  <ItemRow key={it.id} item={it} {...handlers} />
                ))}
              </ul>
            </div>
          )
        ))
      )}

      <style>{`
        .pill-count { display: inline-flex; align-items: center; padding: 0.05rem 0.5rem; border-radius: 9999px; font-size: 0.7rem; background: rgb(71 85 105 / 0.3); color: rgb(203 213 225); }
        .pill-count-urgent { display: inline-flex; align-items: center; padding: 0.05rem 0.5rem; border-radius: 9999px; font-size: 0.7rem; background: rgb(239 68 68 / 0.15); color: rgb(252 165 165); border: 1px solid rgb(239 68 68 / 0.4); }
      `}</style>
    </section>
  );
}

function ItemRow({
  item, busy, onApprove, onReject, onCreateSuggestion, onDismissSuggestion,
}: { item: Item } & ItemHandlers) {
  const approveBusy = busy === `${item.id}:genehmigt`;
  const dismissBusy = busy === `${item.id}:verwerfen`;
  return (
    <li className="px-4 py-2.5 flex items-start gap-3 hover:bg-slate-900/40 transition-colors">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-slate-100 text-sm truncate">{item.primary}</span>
          {item.chips.map((c, i) => (
            <span key={i} className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] ${CHIP_STYLE[c.kind]}`}>
              {c.text}
            </span>
          ))}
        </div>
        <div className="text-xs text-slate-400 mt-0.5 truncate">{item.meta}</div>
        {item.context && <div className="text-xs text-slate-500 mt-0.5 truncate">„{item.context}"</div>}
      </div>
      <div className={`text-xs whitespace-nowrap self-center ${URGENCY_LABEL_STYLE[item.urgencyLevel]}`}>
        {item.ageLabel}
        {item.urgencyLevel === 'urgent' && ' ⚠'}
      </div>
      <div className="flex items-center gap-1 self-center">
        {item.approve && (
          <button
            type="button"
            onClick={() => onApprove(item, item.approve!)}
            disabled={approveBusy}
            className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/30 disabled:opacity-50"
            title="Genehmigen"
          >
            {approveBusy ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} Genehmigen
          </button>
        )}
        {item.suggestion && onCreateSuggestion && (
          <button
            type="button"
            onClick={() => onCreateSuggestion(item)}
            className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/30"
            title="Aufgabe prüfen und anlegen"
          >
            <Plus size={12} /> Anlegen
          </button>
        )}
        {item.suggestion && (
          <button
            type="button"
            onClick={() => onDismissSuggestion(item)}
            disabled={dismissBusy}
            className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-red-500/15 text-red-300 border border-red-500/30 hover:bg-red-500/30 disabled:opacity-50"
            title="Vorschlag verwerfen"
          >
            {dismissBusy ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />} Verwerfen
          </button>
        )}
        {item.reject && (
          <button
            type="button"
            onClick={() => onReject(item)}
            className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-red-500/15 text-red-300 border border-red-500/30 hover:bg-red-500/30"
            title="Ablehnen (Kommentar nötig)"
          >
            <XCircle size={12} /> Ablehnen
          </button>
        )}
        <Link
          to={item.openHref}
          className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-slate-800 text-slate-300 border border-slate-700 hover:bg-slate-700"
          title="Detail öffnen"
        >
          <ExternalLink size={12} /> Öffnen
        </Link>
      </div>
    </li>
  );
}
